import { straicoClient, MODEL_MAPPING } from './straicoService';

export const generateMeetingMinutes = async (formData) => {
  const prompt = buildMeetingMinutesPrompt(formData);

  try {
    const model = MODEL_MAPPING[formData?.selectedModel] || 'openai/gpt-4o-mini';

    console.log('Making Straico API request for meeting minutes with:', {
      model,
      promptLength: prompt.length,
      endpoint: straicoClient.defaults.baseURL + '/prompt/completion'
    });

    // Use the exact same format as the working Job Posting service
    const requestBody = {
      model: model,  // Single model instead of array
      message: prompt,
      temperature: 0.7,
      max_tokens: 3000
    };

    console.log('Meeting minutes request body:', JSON.stringify(requestBody, null, 2));

    const response = await straicoClient.post('/prompt/completion', requestBody);

    console.log('Meeting minutes API Response:', response.data);

    let content = '';

    // Handle different possible response structures - using exact same logic as Job Posting
    if (response.data && response.data.completion) {
      // Direct completion in response
      content = response.data.completion.trim();
    } else if (response.data && response.data.data) {
      // Nested data structure
      const responseData = response.data.data;
      if (responseData.completions) {
        const completions = responseData.completions;
        const modelKey = Object.keys(completions)[0];
        const completion = completions[modelKey];
        
        if (completion && completion.completion && completion.completion.choices) {
          content = completion.completion.choices[0].message.content;
        }
      }
    } else if (response.data && response.data.choices) {
      // OpenAI-like format
      content = response.data.choices[0].message.content;
    }

    if (!content) {
      console.log('No content received from API, trying alternative format...');
      // Try alternative format like Job Posting service does
      return await generateMeetingMinutesAlternative(formData);
    }

    return {
      minutes: content.trim(),
      model: model,
      usage: response.data.usage || {},
      price: response.data.price || null
    };

  } catch (error) {
    console.error('Meeting minutes generation error:', error);

    // Provide detailed error logging
    if (error.response) {
      console.error('API Error Response:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers
      });
    }

    // Try alternative format if the first one fails (like Job Posting does)
    if (error.response?.status === 422 || error.message.includes('422') || error.message.includes('validation failed')) {
      console.log('Primary format failed with 422, trying alternative format...');
      try {
        return await generateMeetingMinutesAlternative(formData);
      } catch (alternativeError) {
        console.log('Alternative format also failed:', alternativeError.message);
        // Fall back to mock only if both formats fail
        return generateMockMeetingMinutes(formData);
      }
    }

    // Only fall back to mock if it's a server error (5xx) or network error
    if (error.response?.status >= 500 || !error.response) {
      console.log('Server error or network issue, generating mock minutes...');
      return generateMockMeetingMinutes(formData);
    }

    // For other client errors, throw the actual error
    let errorMessage = 'Failed to generate meeting minutes.';
    
    if (error.response?.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }

    throw new Error(errorMessage);
  }
};

// Alternative request format to try if the first one fails (like Job Posting service)
export const generateMeetingMinutesAlternative = async (formData) => {
  const prompt = buildMeetingMinutesPrompt(formData);
  
  try {
    const model = MODEL_MAPPING[formData?.selectedModel] || 'openai/gpt-4o-mini';
    
    // Try with models array format (as in Job Posting alternative)
    const requestBody = {
      models: [model],
      message: prompt
    };

    console.log('Trying alternative meeting minutes format:', JSON.stringify(requestBody, null, 2));

    const response = await straicoClient.post('/prompt/completion', requestBody);

    // Process response similar to Job Posting alternative
    if (response.data && response.data.data && response.data.data.completions) {
      const completions = response.data.data.completions;
      const modelKey = Object.keys(completions)[0];
      const completion = completions[modelKey];
      
      if (completion && completion.completion && completion.completion.choices) {
        const content = completion.completion.choices[0].message.content;
        return {
          minutes: content.trim(),
          model: model,
          usage: completion.completion.usage || {},
          price: completion.price || null
        };
      }
    }

    throw new Error('Unable to parse alternative API response');
  } catch (error) {
    throw error;
  }
};

const buildMeetingMinutesPrompt = (formData) => {
  const { 
    transcript, 
    meetingTitle, 
    meetingDate, 
    attendees, 
    tone, 
    includeActionItems, 
    includeSummary, 
    language, 
    notes 
  } = formData;

  let prompt = `You are a professional meeting minutes specialist for municipal government meetings. Transform the following meeting transcript into well-structured, professional meeting minutes suitable for official city records.

Meeting Information:
${meetingTitle ? `Title: ${meetingTitle}` : ''}
${meetingDate ? `Date: ${new Date(meetingDate).toLocaleString()}` : ''}
${attendees ? `Attendees: ${attendees}` : ''}

Transcript:
${transcript}

Requirements:
1. Create professional meeting minutes in ${language} language
2. Use a ${tone} tone throughout the document
3. Structure the content with clear headings and sections
${includeSummary ? '4. Include an executive summary at the beginning' : ''}
${includeActionItems ? '5. Extract and clearly highlight action items with responsible parties and deadlines where mentioned' : ''}
6. Organize discussion points logically by topic
7. Capture key decisions made during the meeting
8. Note any follow-up items or next steps
9. Include voting results if any votes were taken
10. Maintain the professional tone expected in municipal government documentation

Format Guidelines:
- Use clear headings (use ## for main sections, ### for subsections)
- Use bullet points for discussion items
- Use numbered lists for action items
- Bold important decisions or key points
- For action items, use the format: - [ ] Action item description (Responsible: Name, Due: Date)
- For completed items mentioned in transcript: - [x] Completed item description

${notes ? `Additional Context: ${notes}` : ''}

Structure the minutes as follows:

# ${meetingTitle || 'Meeting Minutes'}

**Date:** ${meetingDate ? new Date(meetingDate).toLocaleString() : '[Date]'}
**Attendees:** ${attendees || '[Attendees]'}

${includeSummary ? `## Executive Summary
[Provide a brief 2-3 sentence summary of the meeting's key outcomes and decisions]

` : ''}## Meeting Called to Order
[Note the time the meeting started and who called it to order]

## Agenda Items Discussed

### [Topic 1]
- Discussion points and key comments
- Important decisions made
- Any concerns raised

### [Topic 2]
- Discussion points and key comments
- Important decisions made
- Any concerns raised

## Decisions Made
- **Decision 1:** [Brief description of decision and rationale]
- **Decision 2:** [Brief description of decision and rationale]

${includeActionItems ? `## Action Items
- [ ] Action item 1 (Responsible: Name, Due: Date)
- [ ] Action item 2 (Responsible: Name, Due: Date)

` : ''}## Next Steps
[Any follow-up items or next meeting details]

## Meeting Adjourned
[Time the meeting ended]

Please ensure the minutes are professional, accurate, and capture all important information from the transcript while maintaining the formal tone appropriate for municipal government records.`;

  return prompt;
};

const generateMockMeetingMinutes = (formData) => {
  const meetingTitle = formData.meetingTitle || 'Team Meeting';
  const meetingDate = formData.meetingDate ? new Date(formData.meetingDate).toLocaleString() : new Date().toLocaleString();
  const attendees = formData.attendees || 'Team Members';

  const mockMinutes = `# ${meetingTitle}

**Date:** ${meetingDate}
**Attendees:** ${attendees}

## Executive Summary
This meeting covered project updates, upcoming deadlines, and resource allocation. The team discussed current progress and identified key action items for the next phase.

## Meeting Called to Order
Meeting called to order at 2:00 PM by the Committee Chair.

## Agenda Items Discussed

### Project Status Update
- Current project is 75% complete and progressing well
- No major blockers have been identified at this time
- Timeline remains on track for the original deadline
- Quality metrics are meeting expectations

### Resource Allocation Review
- Additional resources may be needed for the final implementation phase
- Budget review has been scheduled for next week
- Team capacity assessment has been completed
- Current staffing levels are adequate for immediate needs

### Quality Assurance Planning
- Testing phase scheduled to begin next Monday
- QA checklist has been finalized and approved
- Bug tracking system is configured and ready for use
- Test environments have been prepared

### Risk Assessment
- Identified potential risks in the deployment phase
- Mitigation strategies have been developed
- Contingency plans are in place
- Regular risk reviews will continue

## Decisions Made
- **Project Timeline:** Approved to maintain current schedule with weekly check-ins
- **Resource Allocation:** Approved additional QA resources for testing phase
- **Budget Approval:** Authorized additional funds for final phase requirements

## Action Items
- [ ] Complete final development tasks (Responsible: Development Team, Due: Friday)
- [ ] Prepare comprehensive QA test cases (Responsible: QA Team, Due: Monday)
- [ ] Schedule budget review meeting with stakeholders (Responsible: Project Manager, Due: This week)
- [ ] Update project documentation and user guides (Responsible: Technical Writer, Due: Next Tuesday)
- [ ] Conduct risk assessment review (Responsible: Risk Manager, Due: Next Friday)

## Next Steps
- Next meeting scheduled for same time next week
- Weekly progress reports to continue as planned
- Stakeholder presentation planned for month-end
- Preparation for final phase implementation to begin

## Meeting Adjourned
Meeting adjourned at 3:00 PM. All agenda items were covered successfully.

---
*These minutes were generated using AI assistance and should be reviewed for accuracy.*`;

  return {
    minutes: mockMinutes,
    model: 'mock',
    usage: {},
    price: null,
    isMock: true
  };
};

export default { generateMeetingMinutes };